import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import { PaperProps } from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { tokens as t } from "../../theme/tokens";

export const EmployeePageHeader: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ eyebrow, title, description, action }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      flexDirection: { xs: "column", sm: "row" },
      gap: 2,
      mb: 3,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      {eyebrow && (
        <Typography variant="overline" sx={{ color: t.color.primary }}>
          {eyebrow}
        </Typography>
      )}
      <Typography component="h1" variant="h4">
        {title}
      </Typography>
      {description && (
        <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
          {description}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

export const EmployeeSurface: React.FC<PaperProps> = ({ children, sx, ...props }) => (
  <Paper
    variant="outlined"
    sx={{ borderColor: t.color.cardLine, borderRadius: `${t.radius.card}px`, overflow: "hidden", ...(sx as object) }}
    {...props}
  >
    {children}
  </Paper>
);

export const EmployeeLoadingState: React.FC<{ message?: string }> = ({ message = "Loading…" }) => (
  <EmployeeSurface>
    <Box role="status" aria-live="polite" sx={{ minHeight: 240, display: "grid", placeItems: "center", p: 4 }}>
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress size={28} />
        <Typography color="text.secondary" sx={{ mt: 1.5 }}>
          {message}
        </Typography>
      </Box>
    </Box>
  </EmployeeSurface>
);

export const EmployeeErrorState: React.FC<{ message: string }> = ({ message }) => (
  <Alert severity="error" variant="outlined" role="alert" sx={{ bgcolor: t.color.paper }}>
    {message}
  </Alert>
);

export const EmployeeEmptyState: React.FC<{ title: string; description?: string }> = ({ title, description }) => (
  <Box sx={{ py: 5, px: 3, textAlign: "center" }}>
    <Typography variant="h6">{title}</Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        {description}
      </Typography>
    )}
  </Box>
);
